// src/pages/Admin/AdminFeatured.js
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material'
import {
  Star,
  StarBorder,
  ArrowUpward,
  ArrowDownward,
  Search,
  Save,
  Refresh
} from '@mui/icons-material'
import { useAuth } from '../../context/AuthContext'
import { adminAPI } from '../../services/api'
import { API_BASE_URL } from '../../config'

const AdminFeatured = () => {
  const navigate = useNavigate()
  const { user } = useAuth()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const [allProperties, setAllProperties] = useState([])
  const [featuredProperties, setFeaturedProperties] = useState([])

  // Verificar se é admin
  useEffect(() => {
    if (user && !user.is_staff) {
      navigate('/')
    }
  }, [user, navigate])

  useEffect(() => {
    fetchProperties()
  }, [])

  const fetchProperties = async () => {
    try {
      setLoading(true)
      setError('')
      const response = await adminAPI.getSuperFeatured()
      const properties = response.data.results || response.data || []

      // Separar em featured e não featured
      const featured = properties
        .filter((p) => p.is_super_featured)
        .sort((a, b) => a.super_featured_order - b.super_featured_order)
      const notFeatured = properties.filter((p) => !p.is_super_featured)

      setFeaturedProperties(featured)
      setAllProperties(notFeatured)
    } catch (err) {
      console.error('Erro ao carregar propriedades:', err)
      setError(
        'Erro ao carregar propriedades. Verifique se você tem permissão de admin.'
      )
    } finally {
      setLoading(false)
    }
  }

  const addToFeatured = (property) => {
    setAllProperties((prev) => prev.filter((p) => p.id !== property.id))
    setFeaturedProperties((prev) => [
      ...prev,
      {
        ...property,
        is_super_featured: true,
        super_featured_order: prev.length
      }
    ])
    setSuccess('')
  }

  const removeFromFeatured = (property) => {
    setFeaturedProperties((prev) => prev.filter((p) => p.id !== property.id))
    setAllProperties((prev) => [
      { ...property, is_super_featured: false, super_featured_order: 0 },
      ...prev
    ])
    setSuccess('')
  }

  const moveUp = (index) => {
    if (index === 0) return
    const newList = [...featuredProperties]
    ;[newList[index - 1], newList[index]] = [newList[index], newList[index - 1]]
    setFeaturedProperties(newList)
    setSuccess('')
  }

  const moveDown = (index) => {
    if (index === featuredProperties.length - 1) return
    const newList = [...featuredProperties]
    ;[newList[index], newList[index + 1]] = [newList[index + 1], newList[index]]
    setFeaturedProperties(newList)
    setSuccess('')
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError('')
      setSuccess('')

      const featured = featuredProperties.map((p, index) => ({
        id: p.id,
        order: index
      }))

      await adminAPI.updateSuperFeatured(featured)
      setSuccess(`${featured.length} imóvel(is) salvo(s) como Super Destaque!`)
    } catch (err) {
      console.error('Erro ao salvar:', err)
      setError('Erro ao salvar. Tente novamente.')
    } finally {
      setSaving(false)
    }
  }

  const filteredProperties = allProperties.filter(
    (p) =>
      p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.city.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const getImageUrl = (property) => {
    if (property.main_image) {
      return property.main_image.startsWith('http')
        ? property.main_image
        : `${API_BASE_URL}${property.main_image}`
    }
    if (property.images && property.images.length > 0) {
      const img = property.images[0].image
      return img.startsWith('http') ? img : `${API_BASE_URL}${img}`
    }
    return '/placeholder-property.jpg'
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0
    }).format(price)
  }

  if (!user?.is_staff) {
    return (
      <Container maxWidth='lg' sx={{ py: 4 }}>
        <Alert severity='error'>
          Acesso negado. Apenas administradores podem acessar esta página.
        </Alert>
      </Container>
    )
  }

  return (
    <Container maxWidth='xl' sx={{ py: 4 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4
        }}
      >
        <Box>
          <Typography variant='h4' sx={{ fontWeight: 700 }}>
            Gerenciar Super Destaque
          </Typography>
          <Typography color='text.secondary'>
            Selecione os imóveis que aparecerão na seção de destaque da Home
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant='outlined'
            startIcon={<Refresh />}
            onClick={fetchProperties}
            disabled={loading}
          >
            Atualizar
          </Button>
          <Button
            variant='contained'
            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
            onClick={handleSave}
            disabled={saving || loading}
          >
            Salvar Alterações
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity='error' sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity='success' sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={4}>
          {/* Coluna Esquerda - Super Destaque */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3, minHeight: 500 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 3
                }}
              >
                <Star sx={{ color: '#FF7F27' }} />
                <Typography variant='h6' sx={{ fontWeight: 600 }}>
                  Imóveis Mais Procurados ({featuredProperties.length})
                </Typography>
              </Box>

              {featuredProperties.length === 0 ? (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 8,
                    color: 'text.secondary'
                  }}
                >
                  <StarBorder sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                  <Typography>Nenhum imóvel em destaque</Typography>
                  <Typography variant='body2'>
                    Adicione imóveis da lista ao lado
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    maxHeight: 500,
                    overflowY: 'auto',
                    pr: 1
                  }}
                >
                  {featuredProperties.map((property, index) => (
                    <Card
                      key={property.id}
                      sx={{
                        display: 'flex',
                        border: '2px solid #FF7F27',
                        bgcolor: '#FFF8F3'
                      }}
                    >
                      <CardMedia
                        component='img'
                        sx={{
                          width: 120,
                          height: 90,
                          objectFit: 'cover',
                          flexShrink: 0
                        }}
                        image={getImageUrl(property)}
                        alt={property.title}
                      />
                      <CardContent
                        sx={{
                          flex: 1,
                          py: 1.5,
                          px: 2,
                          '&:last-child': { pb: 1.5 },
                          minWidth: 0
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: 1
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Chip
                              label={`#${index + 1}`}
                              size='small'
                              sx={{
                                bgcolor: '#FF7F27',
                                color: 'white',
                                fontWeight: 600,
                                mb: 0.5
                              }}
                            />
                            <Typography
                              variant='body2'
                              sx={{
                                fontWeight: 600,
                                mb: 0.5,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                lineHeight: 1.3
                              }}
                            >
                              {property.title}
                            </Typography>
                            <Typography
                              variant='caption'
                              color='text.secondary'
                              sx={{ display: 'block' }}
                            >
                              {property.city} - {formatPrice(property.price)}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              flexShrink: 0
                            }}
                          >
                            <IconButton
                              size='small'
                              onClick={(e) => {
                                e.stopPropagation()
                                moveUp(index)
                              }}
                              disabled={index === 0}
                            >
                              <ArrowUpward fontSize='small' />
                            </IconButton>
                            <IconButton
                              size='small'
                              onClick={(e) => {
                                e.stopPropagation()
                                moveDown(index)
                              }}
                              disabled={index === featuredProperties.length - 1}
                            >
                              <ArrowDownward fontSize='small' />
                            </IconButton>
                          </Box>
                        </Box>
                      </CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          pr: 1,
                          flexShrink: 0
                        }}
                      >
                        <IconButton
                          color='error'
                          onClick={(e) => {
                            e.stopPropagation()
                            removeFromFeatured(property)
                          }}
                          title='Remover do destaque'
                        >
                          <StarBorder />
                        </IconButton>
                      </Box>
                    </Card>
                  ))}
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Coluna Direita - Todos os Imóveis */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3, minHeight: 500 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 3
                }}
              >
                <Typography variant='h6' sx={{ fontWeight: 600 }}>
                  Imóveis Disponíveis ({filteredProperties.length})
                </Typography>
              </Box>

              <TextField
                fullWidth
                size='small'
                placeholder='Buscar por título ou cidade...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Search />
                    </InputAdornment>
                  )
                }}
              />

              <TableContainer sx={{ maxHeight: 500 }}>
                <Table stickyHeader size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100' }}>Imagem</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100' }}>Título</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100' }}>Cidade</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100' }}>Preço</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.100', width: 60 }} align='center'>Ação</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredProperties.map((property) => (
                      <TableRow
                        key={property.id}
                        hover
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { bgcolor: '#FFF8F3' }
                        }}
                        onClick={() => addToFeatured(property)}
                      >
                        <TableCell sx={{ p: 1 }}>
                          <Box
                            component='img'
                            src={getImageUrl(property)}
                            alt={property.title}
                            sx={{
                              width: 80,
                              height: 60,
                              objectFit: 'cover',
                              borderRadius: 1
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant='body2'
                            sx={{
                              fontWeight: 600,
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                            title={property.title}
                          >
                            {property.title}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant='body2' color='text.secondary'>
                            {property.city}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant='body2'
                            sx={{ fontWeight: 700, color: 'primary.main' }}
                          >
                            {formatPrice(property.price)}
                          </Typography>
                        </TableCell>
                        <TableCell align='center'>
                          <IconButton
                            color='primary'
                            size='small'
                            title='Adicionar ao destaque'
                          >
                            <Star />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {filteredProperties.length === 0 && (
                  <Box
                    sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}
                  >
                    <Typography>Nenhum imóvel encontrado</Typography>
                  </Box>
                )}
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Container>
  )
}

export default AdminFeatured
